// Who you are actually fighting: which army's *rules* a side plays by, and
// which evaluation is choosing its moves.
//
// Both are already on the board or already decided, and the profile is
// invisible even on the screen that sets it.
//
// Pure, and separate from the shelf for the reason `pieceBadges.ts` is: the
// interesting cases are a human opponent and a run's camp, neither of which
// wants a browser to test.

import { DEFAULT_PROFILE, profileById, profileForSide } from "src/engine/ai/profiles";
import type { Color } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { KING_FACTORY } from "src/engine/rules/position";

export interface ArmyView {
  /** What this side's rules are. Always answered. */
  army: string;
  armyDetail: string;
  /**
   * The evaluation choosing its moves, when a machine is choosing them.
   *
   * **Absent for a human**, and that is the point of asking. A profile is what
   * `useSoloOpponent` hands the planner; in hot-seat or over the network
   * nobody consults one, and naming a profile there would be a plain untruth
   * about who you are playing.
   */
  profile?: string;
  profileDetail?: string;
}

/** Each faction's name and line (`KING_KIND`). Only the factory army is left (§31 R2). */
const FACTION_NAMES: Record<number, { army: string; armyDetail: string }> = {
  [KING_FACTORY]: {
    army: "Factory",
    armyDetail: "The ordinary game. The king refines, the rooks mint, the pawns dig."
  }
};

/**
 * What the side holding `square`'s piece is playing, as the shelf says it.
 *
 * `profiles` is the scenario screen's per-side override and beats the style's
 * own, exactly as `useSoloOpponent` resolves it — a second reading of that
 * would let the shelf name one evaluation while the planner used another.
 */
export function armyView(
  state: GameState,
  side: Color,
  profiles: readonly (string | undefined)[] | undefined,
  machinePlays: boolean
): ArmyView {
  const position = state.position;
  const kind = position.kingKind[side];
  const named = FACTION_NAMES[kind ?? KING_FACTORY] ?? {
    army: "Unknown",
    armyDetail: "An army this build has no name for."
  };

  if (!machinePlays) return named;

  // The same resolution the driver makes, in the same order — including the
  // run's own say, which is why this asks `profileForSide`. A shelf naming one
  // evaluation while a camp plays another is the drift this refuses.
  const profile = profileById(profiles?.[side] ?? profileForSide(state, side) ?? DEFAULT_PROFILE);
  return { ...named, profile: profile.name, profileDetail: profile.detail };
}
